import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, X, Key, CheckCircle2, ShieldAlert, Phone, ArrowLeft } from 'lucide-react';
import { authService } from '../services/api';
import { SubdomainLink } from '../components/SubdomainLink';
import { isCrossSubdomain, getSubdomainUrl } from '../utils/subdomain';
import { useAuth } from '../hooks/useAuth';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  const handleBackClick = () => {
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');

    const isAuthPath = (path) => {
      if (!path) return false;
      const lowerPath = path.toLowerCase();
      return lowerPath.startsWith('/admin') || lowerPath.startsWith('/my-account');
    };

    if (redirectParam) {
      const target = decodeURIComponent(redirectParam);
      if (isAuthPath(target)) {
        navigate('/');
      } else {
        navigate(target);
      }
      return;
    }

    if (location.state?.from) {
      const fromPath = typeof location.state.from === 'string'
        ? location.state.from
        : location.state.from.pathname || '';
      
      if (isAuthPath(fromPath)) {
        navigate('/');
        return;
      }
    }

    navigate(-1);
  };


  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  // Track which fields the user has touched (to avoid showing errors before user interacts)
  const [touched, setTouched] = useState({
    username: false,
    password: false
  });

  // --- Validation Rules ---
  const validate = (data) => {
    const errors = {};

    if (!data.username.trim()) {
      errors.username = 'Email / Mobile Number is required.';
    }

    if (!data.password) {
      errors.password = 'Password is required.';
    }

    return errors;
  };

  const fieldErrors = validate(formData);

  const handleBlur = (e) => {
    setTouched({ ...touched, [e.target.name]: true });
  };

  // Helper: returns classes for an input based on validation state
  const inputClass = (fieldName) => {
    const hasError = touched[fieldName] && fieldErrors[fieldName];
    if (hasError) {
      return 'block w-full pl-10 pr-3 py-3 border-2 border-red-400 rounded-lg bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700';
    }
    return 'block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:outline-none';
  };

  const passwordInputClass = () => {
    const hasError = touched.password && fieldErrors.password;
    if (hasError) {
      return 'block w-full pl-10 pr-12 py-3 border-2 border-red-400 rounded-lg bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700';
    }
    return 'block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:outline-none';
  };

  const iconClass = (fieldName) => {
    const hasError = touched[fieldName] && fieldErrors[fieldName];
    if (hasError) return 'absolute inset-y-0 left-0 pl-3 flex items-center text-red-400';
    return 'absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400';
  };

  // Forgot Password States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = request mobile, 2 = verify & reset, 3 = success
  const [forgotMobile, setForgotMobile] = useState('');
  const [retrievedName, setRetrievedName] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotPwd, setShowForgotPwd] = useState(false);
  const [showForgotConfirmPwd, setShowForgotConfirmPwd] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess('');

    if (!/^[0-9]{10}$/.test(forgotMobile)) {
      setForgotError("Please enter a valid 10-digit mobile number.");
      setForgotLoading(false);
      return;
    }

    const result = await authService.forgotPasswordRequest(forgotMobile);
    if (result.success) {
      setMaskedMobile(result.data.mobileMasked || 'XXXX');
      setRetrievedName(result.data.name || '');
      setForgotSuccess(`OTP has been sent to your registered mobile number: ${result.data.mobileMasked}`);
      setForgotStep(2);
    } else {
      setForgotError(result.error || "Failed to request OTP. Please try again.");
    }
    setForgotLoading(false);
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess('');

    if (forgotPassword !== forgotConfirmPassword) {
      setForgotError("Passwords do not match.");
      setForgotLoading(false);
      return;
    }

    if (forgotPassword.length < 8) {
      setForgotError("Password must be at least 8 characters long.");
      setForgotLoading(false);
      return;
    }

    const result = await authService.forgotPasswordVerify(forgotMobile, forgotOtp, forgotPassword);
    if (result.success) {
      setForgotSuccess("Your password has been reset successfully!");
      setForgotStep(3);
      setTimeout(() => {
        setShowForgotModal(false);
        // Reset states
        setForgotMobile('');
        setRetrievedName('');
        setForgotOtp('');
        setForgotPassword('');
        setForgotConfirmPassword('');
        setForgotStep(1);
        setForgotSuccess('');
      }, 3000);
    } else {
      setForgotError(result.error || "Failed to reset password. Please try again.");
    }
    setForgotLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched on submit attempt
    setTouched({ username: true, password: true });

    // Block submit if any field-level errors
    if (Object.keys(validate(formData)).length > 0) return;

    setLoading(true);
    setError('');
    
    const result = await authService.login(formData);
    
    if (result.success) {
      let isAdmin = false;
      try {
        const token = result.data.accessToken;
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const roles = payload.roles ? payload.roles.split(',').map(r => r.trim()) : [];
            isAdmin = roles.some(r => [
              'ROLE_SUPER_ADMIN',
              'ROLE_SALES_ADMIN',
              'ROLE_LISTING_ADMIN',
              'ROLE_SUPPORT_ADMIN',
              'ROLE_ADMIN',
              'ROLE_MAGAZINE_SALES'
            ].includes(r));
          }
        }
      } catch (err) {
        console.error("Error decoding token for admin redirect:", err);
      }

      const searchParams = new URLSearchParams(location.search);
      const redirectParam = searchParams.get('redirect');
      
      const rawFrom = redirectParam 
        ? decodeURIComponent(redirectParam) 
        : (location.state?.from || (isAdmin ? '/admin' : '/my-account/manage-listing'));

      let fromPath = '/my-account/manage-listing';
      if (typeof rawFrom === 'string') {
        fromPath = rawFrom;
      } else if (rawFrom && rawFrom.pathname) {
        fromPath = rawFrom.pathname + (rawFrom.search || '') + (rawFrom.hash || '');
      }

      if (isCrossSubdomain(fromPath)) {
        window.location.replace(getSubdomainUrl(fromPath));
      } else {
        navigate(fromPath);
      }
    } else {
      setError(String(result.error));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Left Side - Matching Design */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A734E] text-white p-20 flex-col justify-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#23855B] rounded-full -mr-48 -mt-48 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#23855B] rounded-full -ml-40 -mb-40 opacity-50"></div>
        
        <div className="relative z-10 max-w-md">
          <h1 className="text-5xl font-bold mb-8">Welcome Back</h1>
          <p className="text-xl leading-relaxed opacity-90 mb-16">
            Log in to manage your vendor profile, view your network, and connect with India's top architects.
          </p>
          
          <div className="flex space-x-16">
            <div>
              <p className="text-4xl font-bold mb-2">2,500+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Vendors</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">50+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 bg-[#F9FAF7] flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <button
              type="button"
              onClick={handleBackClick}
              className="inline-flex items-center text-sm font-bold text-[#1A734E] hover:text-[#155D3F] transition-colors group bg-transparent border-0 p-0 cursor-pointer"
            >
              <ArrowLeft size={16} className="mr-1.5 transform group-hover:-translate-x-0.5 transition-transform" />
              Back
            </button>
          </div>
          <div className="text-left">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Login</h2>
            <p className="text-gray-500">Welcome back! Please enter your details.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-6">
            {error && <p className="text-red-500 bg-red-50 p-3 rounded-lg text-sm">{error}</p>}
            
            <div className="space-y-4">
              {/* Email / Mobile Number */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Email / Mobile Number <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <span className={iconClass('username')}>
                    <Mail size={18} />
                  </span>
                  <input
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClass('username')}
                    placeholder="Enter email or mobile number"
                  />
                </div>
                {touched.username && fieldErrors.username && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.username}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Password <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <span className={iconClass('password')}>
                    <Lock size={18} />
                  </span>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={passwordInputClass()}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.password && fieldErrors.password && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.password}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-600">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForgotModal(true);
                    setForgotStep(1);
                    setForgotError('');
                    setForgotSuccess('');
                    setForgotMobile('');
                    setRetrievedName('');
                  }}
                  className="text-[#1A734E] font-bold hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-lg font-bold text-white bg-[#1A734E] hover:bg-[#155D3F] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-gray-500">
                Don't have an account? <Link to="/sign-up" state={location.state} className="text-[#1A734E] font-bold">Create Account</Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-gray-100 flex flex-col relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              onClick={() => setShowForgotModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>

            <div className="text-left mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Forgot Password</h3>
              <p className="text-sm text-gray-500 mt-1">
                {forgotStep === 1 && "Enter your registered mobile number to search for your account and receive an OTP code."}
                {forgotStep === 2 && `We've sent a 6-digit code to your registered mobile number: ${maskedMobile}.`}
                {forgotStep === 3 && "Password updated successfully!"}
              </p>
            </div>

            {/* Error & Success Badges */}
            {forgotError && (
              <div className="bg-rose-50 text-rose-800 border border-rose-100 p-3.5 rounded-xl text-xs flex items-center gap-2.5 mb-5 font-semibold">
                <ShieldAlert size={16} className="text-rose-500 flex-shrink-0" />
                <span>{forgotError}</span>
              </div>
            )}
            {forgotSuccess && (
              <div className="bg-emerald-50 text-emerald-800 border border-emerald-100 p-3.5 rounded-xl text-xs flex items-center gap-2.5 mb-5 font-semibold">
                <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                <span>{forgotSuccess}</span>
              </div>
            )}

            {forgotStep === 1 && (
              <form onSubmit={handleRequestOtp} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Registered Mobile Number <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      <Phone size={18} />
                    </span>
                    <input
                      type="text"
                      maxLength={10}
                      required
                      value={forgotMobile}
                      onChange={(e) => setForgotMobile(e.target.value.replace(/[^0-9]/g, ''))}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E] focus:border-transparent transition-all text-sm"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="w-1/2 py-3 border border-gray-200 text-gray-600 rounded-lg text-sm font-bold hover:bg-gray-50 active:scale-[0.98] transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-1/2 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-lg text-sm font-bold active:scale-[0.98] transition-all shadow-sm shadow-[#1A734E]/10 flex items-center justify-center disabled:opacity-50"
                  >
                    {forgotLoading ? "Checking..." : "Verify & Send OTP"}
                  </button>
                </div>
              </form>
            )}

            {forgotStep === 2 && (
              <form onSubmit={handleVerifyAndReset} className="space-y-5">
                {retrievedName && (
                  <div className="text-lg font-bold text-[#1A734E] mb-2">
                    Hi, {retrievedName}!
                  </div>
                )}
                {/* OTP Code */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    6-Digit OTP Code <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      <Key size={18} />
                    </span>
                    <input
                      type="text"
                      maxLength={6}
                      required
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/[^0-9]/g, ''))}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E] focus:border-transparent transition-all text-sm tracking-widest font-mono text-center font-bold"
                      placeholder="XXXXXX"
                    />
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    New Password <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      <Lock size={18} />
                    </span>
                    <input
                      type={showForgotPwd ? "text" : "password"}
                      required
                      value={forgotPassword}
                      onChange={(e) => setForgotPassword(e.target.value)}
                      className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E] focus:border-transparent transition-all text-sm"
                      placeholder="Minimum 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowForgotPwd(!showForgotPwd)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showForgotPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Confirm Password <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                      <Lock size={18} />
                    </span>
                    <input
                      type={showForgotConfirmPwd ? "text" : "password"}
                      required
                      value={forgotConfirmPassword}
                      onChange={(e) => setForgotConfirmPassword(e.target.value)}
                      className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E] focus:border-transparent transition-all text-sm"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowForgotConfirmPwd(!showForgotConfirmPwd)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showForgotConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotStep(1);
                      setForgotOtp('');
                      setForgotError('');
                      setForgotSuccess('');
                      setRetrievedName('');
                    }}
                    className="w-1/3 py-3 border border-gray-200 text-gray-600 rounded-lg text-sm font-bold hover:bg-gray-50 active:scale-[0.98] transition-all"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-2/3 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-lg text-sm font-bold active:scale-[0.98] transition-all shadow-sm shadow-[#1A734E]/10 flex items-center justify-center disabled:opacity-50"
                  >
                    {forgotLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>
            )}

            {forgotStep === 3 && (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto text-emerald-500 border border-emerald-100">
                  <CheckCircle2 size={36} />
                </div>
                <h4 className="text-xl font-bold text-gray-900">Success!</h4>
                <p className="text-sm text-gray-500">Your password has been successfully reset. Redirecting you to login...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
